import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
} from '@mui/material';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from "primereact/dropdown";
import apiService from '../../services/apiservice';
import { toast } from "react-toastify";

const ROLE_CODES = {
  PRADMIN: 'PRADMIN',
  GROUPPRADMIN: 'GROUPPRADMIN',
  CE: 'CE',
  BO: 'BO',
};

const ASSIGNMENT_TYPES = {
  SELF: 'self',
  ASSIGN: 'assign',
};

const OPTIONS = [
  { label: 'Self Assign', value: ASSIGNMENT_TYPES.SELF },
  { label: 'Assign to User', value: ASSIGNMENT_TYPES.ASSIGN }
];

const AssignEnquiry = (props) => {
  const { userType, selected, onClose } = props;
  
  const userObj = JSON.parse(localStorage.getItem("loginObj")) || {};  
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedOption, setSelectedOption] = useState(ASSIGNMENT_TYPES.ASSIGN);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);

  const targetRoleCode = useMemo(() => {
    return userType === ROLE_CODES.BO ? ROLE_CODES.GROUPPRADMIN : ROLE_CODES.CE;
  }, [userType]);

  const handleOptionChange = useCallback((event) => {
    setSelectedOption(event.value);
  }, []);

  const handleUserChange = useCallback((e) => {
    setSelectedUser(e.target.value);
  }, []);

  const getUserNameByCode = useCallback((code) => {
    const user = userList.find(item => item.code === code);
    return user ? user.userName : '';
  }, [userList]);

  const getUserRoleByCode = useCallback((code) => {
    const user = userList.find(item => item.code === code);
    return user ? user.roleCode : '';
  }, [userList]);

  const validateForm = useCallback(() => {
    if (selectedOption === ASSIGNMENT_TYPES.ASSIGN && !selectedUser) {
      toast.error('Please select user');
      return false;
    }
    return true;
  }, [selectedOption, selectedUser]);

  const save = useCallback(async () => {
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      const userCode = selectedOption === ASSIGNMENT_TYPES.SELF 
        ? userObj.user?.code 
        : selectedUser;
      
      const userName = getUserNameByCode(userCode);
      const userRole = getUserRoleByCode(userCode);

      if (!userName || !userRole) {
        toast.error('Invalid user selection');
        setLoading(false);
        return;
      }

      const payload = {
        enquiryId: selected?.id,
        userCode,
        userName,
        roleCode: userRole,
        modifiedBy: userObj.user?.code || '',
        type: "ASSIGN"
      };

      const res = await apiService.updateAssidgn(payload);
      
      if (res && !res.message) {
        toast.success('User Assigned Successfully');
        onClose();
      } else {
        toast.error(res?.message || 'Failed to assign user');
      }
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error('Failed to assign user');
    } finally {
      setLoading(false);
    }
  }, [
    selectedOption, 
    selectedUser, 
    userObj, 
    selected, 
    onClose, 
    validateForm, 
    getUserNameByCode, 
    getUserRoleByCode
  ]);

  const getUserList = useCallback(async () => {
    try {
      const payload = {
        roleCode: targetRoleCode,
        status: 1
      };

      const res = await apiService.getUserList(payload);
      
      if (res?.data?.successList?.length > 0) {
        setUserList(res.data.successList);
      } else {
        setUserList([]);
        toast.info('No users found');
      }
    } catch (error) {
      console.error('Error fetching user list:', error);
      toast.error('Failed to load user list');
      setUserList([]);
    }
  }, [targetRoleCode]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const renderContent = () => {
    if (userType === ROLE_CODES.BO) {
      return (
        <div>
          <Typography variant="h6" gutterBottom>
            Please select your option:
          </Typography>

          <div className="radio-button-container justify-items-center mt-16">
            {OPTIONS.map((option) => (
              <div key={option.value}>
                <div className="radio-button-item">
                  <RadioButton
                    inputId={`option-${option.value}`}
                    name="options"
                    value={option.value}
                    onChange={handleOptionChange}
                    checked={selectedOption === option.value}
                  />
                  <label className='ml-4' htmlFor={`option-${option.value}`}>
                    {option.label}
                  </label>
                </div>
              </div>
            ))}
            
            {selectedOption === ASSIGNMENT_TYPES.SELF && (
              <div className="mr-5 justify-end">
                <Button
                  variant="contained"
                  className="w-32 h-9 bg-[#004172] text-white rounded-md"
                  onClick={save}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>

          {selectedOption === ASSIGNMENT_TYPES.ASSIGN && userType === ROLE_CODES.BO && (
            <div>
              <div className='mt-6'>
                <Typography variant="h6" gutterBottom>
                  Please select user to assign:
                </Typography>
              </div>

              <div className='mt-6 flex grid grid-cols-2 gap-5'>
                <Dropdown
                  options={userList}
                  optionLabel="userName"
                  placeholder="Select a user"
                  optionValue='code'
                  value={selectedUser}
                  onChange={handleUserChange}
                  filter
                  style={{ height: '50px' }}
                  className="border border-gray-300 rounded-md p-2"
                />
                <div className="mt-2 justify-end">
                  <Button
                    variant="contained"
                    className="w-32 h-9 bg-[#004172] text-white rounded-md"
                    onClick={save}
                    disabled={loading || !selectedUser}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (userType === ROLE_CODES.CE) {
      return (
        <div>
          <div className='mt-6'>
            <Typography variant="h6" gutterBottom>
              Please select Concierge Executive to assign:
            </Typography>
          </div>

          <div className='mt-6 flex grid grid-cols-2 gap-5'>
            <Dropdown
              options={userList}
              optionLabel="userName"
              placeholder="Select a user"
              optionValue='code'
              value={selectedUser}
              onChange={handleUserChange}
              filter
              style={{ height: '50px' }}
              className="border border-gray-300 rounded-md p-2"
            />
            <div className="mt-2 justify-end">
              <Button
                variant="contained"
                className="w-32 h-9 bg-[#004172] text-white rounded-md"
                onClick={save}
                disabled={loading || !selectedUser}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderContent()}
    </>
  );
};

AssignEnquiry.propTypes = {
  userType: PropTypes.oneOf([ROLE_CODES.BO, ROLE_CODES.CE]).isRequired,
  selected: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AssignEnquiry;